import { DentalSpecialty } from '~/types/specialty';
import type { SpecialtyFormConfig } from '~/types/specialty';

import { endodonticConfig } from './endodontic';
import { generalDentistryConfig } from './general-dentistry';
import { hospitalConfig } from './hospital';
import { oralSurgeryConfig } from './oral-surgery';
import { orthodonticConfig } from './orthodontic';
import { pediatricsConfig } from './pediatrics';
import { periodontalConfig } from './periodontal';
import { prosthodonticConfig } from './prosthodontic';
import { tmjConfig } from './tmj';

/**
 * Map of all specialty configurations by specialty key
 */
export const SPECIALTY_FORM_CONFIGS: Record<string, SpecialtyFormConfig> = {
  [DentalSpecialty.ENDODONTIC]: endodonticConfig,
  [DentalSpecialty.ORAL_SURGERY]: oralSurgeryConfig,
  [DentalSpecialty.PERIODONTAL]: periodontalConfig,
  [DentalSpecialty.PEDIATRICS]: pediatricsConfig,
  [DentalSpecialty.HOSPITAL]: hospitalConfig,
  [DentalSpecialty.PROSTHODONTIC]: prosthodonticConfig,
  [DentalSpecialty.ORTHODONTIC]: orthodonticConfig,
  [DentalSpecialty.TMJ]: tmjConfig,
  [DentalSpecialty.GENERAL_DENTISTRY]: generalDentistryConfig,
};

/**
 * Get specialty configuration by specialty key
 * @param specialty The specialty key or display name
 * @returns The specialty configuration or undefined if not found
 */
export function getSpecialtyConfig(specialty: string): SpecialtyFormConfig | undefined {
  // First, try to find it directly in the configs
  if (SPECIALTY_FORM_CONFIGS[specialty]) {
    return SPECIALTY_FORM_CONFIGS[specialty];
  }

  // If not found directly, try case-insensitive search
  const lowerCaseSpecialty = specialty.toLowerCase();

  // Check by specialty value
  for (const key in SPECIALTY_FORM_CONFIGS) {
    if (key.toLowerCase() === lowerCaseSpecialty) {
      return SPECIALTY_FORM_CONFIGS[key];
    }
  }

  // Check by displayName
  for (const key in SPECIALTY_FORM_CONFIGS) {
    if (SPECIALTY_FORM_CONFIGS[key].displayName.toLowerCase() === lowerCaseSpecialty) {
      return SPECIALTY_FORM_CONFIGS[key];
    }
  }

  // Check by specialty value (case-insensitive)
  for (const key in SPECIALTY_FORM_CONFIGS) {
    if (SPECIALTY_FORM_CONFIGS[key].specialty.toLowerCase() === lowerCaseSpecialty) {
      return SPECIALTY_FORM_CONFIGS[key];
    }
  }

  // Not found
  return undefined;
}

/**
 * Get all available specialty options for dropdown menus
 * @returns Array of specialty options with value and label
 */
export function getSpecialtyOptions(): Array<{ value: string; label: string }> {
  return Object.values(SPECIALTY_FORM_CONFIGS).map(config => ({
    value: config.specialty,
    label: config.displayName,
  }));
}

/**
 * Map a specialty name from the API to our internal specialty key
 * @param apiSpecialtyName The specialty name from the API
 * @returns The corresponding DentalSpecialty value or the original name if not found
 */
export function mapApiSpecialtyToInternalKey(apiSpecialtyName: string): string {
  // Check if it matches one of our DentalSpecialty values directly
  for (const key in DentalSpecialty) {
    if (DentalSpecialty[key as keyof typeof DentalSpecialty] === apiSpecialtyName) {
      return DentalSpecialty[key as keyof typeof DentalSpecialty];
    }
  }

  // If not found, return the original
  return apiSpecialtyName;
}

export {
  endodonticConfig,
  generalDentistryConfig,
  oralSurgeryConfig,
  periodontalConfig,
  pediatricsConfig,
  hospitalConfig,
  prosthodonticConfig,
  orthodonticConfig,
  tmjConfig,
};
